import { useMutation } from "@apollo/client/react";
import { UploadOutlined } from "@ant-design/icons";
import { App, Button, Empty, Flex, Image, Masonry, Space, Upload, type UploadProps } from "antd";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  AddFileToCollectionDocument,
  type CollectionFileFragment,
  RemoveFileFromCollectionDocument,
} from "../../../gql/generated/graphqlSchema.tsx";
import { DeletableImageFile } from "../../../components/DeletableImageFile.tsx";
import { PhoneUploadModal } from "../../../components/PhoneUploadModal.tsx";
import { useSaving } from "../../../hooks/useSaving.tsx";
import { useUploadProgress } from "../../../hooks/useUploadProgress.ts";
import { useRecipe } from "../RecipeContext.tsx";
import { MAX_UPLOAD_SIZE_BYTES, uploadResponseSchema } from "../../../utils/upload.ts";

const ACCEPTED_TYPES = "image/jpeg,image/png,image/webp";

export const RecipePhotosTab = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { recipe, setRecipe } = useRecipe();
  const { trackSaving } = useSaving();
  const [addFile] = useMutation(AddFileToCollectionDocument);
  const [removeFile] = useMutation(RemoveFileFromCollectionDocument);

  const { progress: uploadPercent, uploading, trackFile, completeFile, failFile } = useUploadProgress();

  const addStagingFileToCollection = async (stagingFileId: string) => {
    const result = await addFile({
      variables: { collectionId: recipe.photoCollectionId, stagingFileId },
    });
    if (result.data) {
      setRecipe({ ...recipe, photos: result.data.addFileToCollection });
    }
  };

  const handleChange: UploadProps["onChange"] = ({ file }) => {
    if (file.status === "uploading") {
      trackFile(file.uid, Math.round(file.percent ?? 0));
    }

    if (file.status === "done") {
      completeFile(file.uid);
      const parsed = uploadResponseSchema.safeParse(file.response);
      if (parsed.success) {
        trackSaving(async () => {
          for (const { stagingFileId } of parsed.data) {
            await addStagingFileToCollection(stagingFileId);
          }
        });
      }
    }

    if (file.status === "error") {
      failFile(file.uid);
      void message.error(t("ingredients.photos.uploadFailed"));
    }
  };

  const handleDelete = useCallback(
    (photoId: string) =>
      trackSaving(() =>
        removeFile({
          variables: { collectionId: recipe.photoCollectionId, fileId: photoId },
        }).then((result) => {
          if (result.data) {
            setRecipe({ ...recipe, photos: result.data.removeFileFromCollection });
          }
        }),
      ),
    [recipe, removeFile, setRecipe, trackSaving],
  );

  const renderPhoto = useCallback(
    (itemInfo: { data: CollectionFileFragment }) => {
      const photo = itemInfo.data;
      return <DeletableImageFile file={photo} onDelete={() => handleDelete(photo.id)} />;
    },
    [handleDelete],
  );

  const masonryItems = useMemo(() => recipe.photos.map((photo) => ({ key: photo.id, data: photo })), [recipe.photos]);

  const uploadControls = (
    <Space.Compact>
      <Upload
        accept={ACCEPTED_TYPES}
        action="/i/upload/staging"
        beforeUpload={(file) => {
          if (file.size > MAX_UPLOAD_SIZE_BYTES) {
            void message.error(t("ingredients.photos.tooLarge"));
            return Upload.LIST_IGNORE;
          }
          return true;
        }}
        multiple
        onChange={handleChange}
        pastable
        showUploadList={false}
      >
        <Button color="primary" icon={<UploadOutlined />} loading={uploading} variant="outlined">
          {uploading ? `${t("phone.uploading")} ${uploadPercent}%` : t("ingredients.photos.upload")}
        </Button>
      </Upload>
      <PhoneUploadModal onSelect={(stagingFileId) => trackSaving(() => addStagingFileToCollection(stagingFileId))} />
    </Space.Compact>
  );

  if (recipe.photos.length === 0) {
    return <Empty description={t("ingredients.photos.empty")}>{uploadControls}</Empty>;
  }

  return (
    <Flex gap="middle" vertical>
      {uploadControls}

      <Image.PreviewGroup>
        <Masonry
          columns={{ xs: 2, sm: 3, md: 4, lg: 5 }}
          gutter={[8, 8]}
          itemRender={renderPhoto}
          items={masonryItems}
        />
      </Image.PreviewGroup>
    </Flex>
  );
};
